import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';

export interface RowAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
}

/** A compact "⋯" overflow menu for per-row actions (edit / delete / rename / ungroup). */
const RowActionsMenu: React.FC<{ actions: RowAction[]; label?: string; disabled?: boolean }> = ({
  actions,
  label = 'Element actions',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen((o) => !o); }}
        disabled={disabled}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open && !disabled}
        className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
      {open && !disabled && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 py-1"
        >
          {actions.map((a, i) => (
            <button
              key={i}
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick(); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                a.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'
              }`}
            >
              {a.icon}
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RowActionsMenu;
